import React, { useEffect, useState } from "react";
import { Col, Row, Spinner, Button } from "reactstrap";
import { useSelector } from "react-redux";
import { Table, Column, AutoSizer } from "react-virtualized";
import "react-virtualized/styles.css";
import formatters from "../../../config/formatters";
import ReactSelect, { components } from "react-select";
import useDashboardTables from "./useDashboardTables";
import { useLocation } from "react-router-dom";
import EmptyDataMessage from "../../../sharedComponents/ErrorMessages/EmptyDataMessage";
import { selectStyles, Theme } from "../../../config/constants";
import { BsCaretDownFill } from "react-icons/bs";
import { Tooltip } from "@mui/material";
import { IoMdInformationCircle } from "react-icons/io";

const LostLocationsTable = () => {
    const location = useLocation();
    const isLoading = useSelector(({ dashboard }) => dashboard.lostLocationsIsLoading);
    const lostLocationsData = useSelector(({ dashboard }) => dashboard.lostLocations ?? []);
    const usaIncluded = useSelector(({ dashboard }) => dashboard.appliedUsaIncluded)
    const { onSelectChange, onClickSeeMore } = useDashboardTables.useLostLocationsTable();
    const { usCurrencyFormatter } = formatters();
    const pathname = location.pathname;
    const isDashboard = pathname === "/dashboard";
    const baseOptions = [
        { value: "Total Volume", label: "Sort By: Total Volume ($)" },
        { value: "Cases", label: "Sort By: Cases" },
        { value: "Locations", label: "Sort By: Locations" },
    ];
    const [selectedOption, setSelectedOption] = useState(baseOptions[0]);
    const lostLocationsOptions = usaIncluded ? baseOptions : baseOptions.filter(opt => opt.value !== "Total Volume");

    useEffect(() => {
        setSelectedOption(lostLocationsOptions[0]);
        // eslint-disable-next-line
    }, [usaIncluded]);

    useEffect(() => {
        onSelectChange(selectedOption.value, lostLocationsData);
        // eslint-disable-next-line
    }, [selectedOption]);

    const DropdownIndicator = (props) => (
        <components.DropdownIndicator {...props}>
            <BsCaretDownFill />
        </components.DropdownIndicator>
    );

    const slideDrawerContents = (
        <Row className="me-2" style={{ height: "90%" }}>
            <AutoSizer>
                {({ height, width }) => (
                    <Table
                        width={width}
                        height={height}
                        headerHeight={40}
                        rowHeight={40}
                        rowCount={lostLocationsData?.length}
                        rowGetter={({ index }) => lostLocationsData?.[index]}
                        className="table lostLocationsTable border-grayLight text-size-14"
                        headerClassName="table-header"
                        rowClassName="table-row"
                    >
                        <Column
                            label="LOCATIONS"
                            dataKey="location_name"
                            width={usaIncluded ? width * 0.4 : width * 0.6}
                        />

                        {usaIncluded && (
                            <Column 
                                label="TOTAL VOLUME ($)" 
                                dataKey="total_volume" 
                                width={width * 0.3} 
                                cellRenderer={({ cellData }) => usCurrencyFormatter.format(cellData)} 
                            />
                        )}

                        <Column
                            label="CASES"
                            dataKey="total_cases"
                            width={usaIncluded ? width * 0.3 : width * 0.4}
                        />
                    </Table>
                )}
            </AutoSizer>
        </Row>
    );

    if (isLoading || lostLocationsData.length === 0) {
        return (
            <Col>
                <Row className="pb-2">
                    <Col className={window.innerWidth < 1024 ? "col-7" : "col-5"}>
                        <ReactSelect
                            styles={selectStyles}
                            components={{ DropdownIndicator }}
                            options={lostLocationsOptions}
                            defaultValue={lostLocationsOptions[0]}
                        />
                    </Col>
                    <Col className="d-flex align-items-center px-0 my-auto">
                        <Tooltip
                            title="Lost locations refer to locations that purchased from you previously but have not purchased ytd. This may include some locations that have left our platform."
                            arrow
                        >
                            <div className="py-auto">
                                <IoMdInformationCircle
                                    className="text-size-18"
                                    style={{ color: Theme().colors.accent }}
                                />
                            </div>
                        </Tooltip>
                    </Col>
                </Row>
                <Row style={{ height: "240px" }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ height: "100%" }}>
                        {isLoading ? (
                            <Spinner color="beplBlue" animation="border" />
                        ) : (
                            <EmptyDataMessage />
                        )}
                    </div>
                </Row>
            </Col>
        );
    }

    return (
        <Col style={{ height: "100%" }}>
            <Row className="pb-2">
                <Col className={window.innerWidth < 1024 ? "col-7" : "col-5"}>
                    <ReactSelect
                        styles={selectStyles}
                        components={{ DropdownIndicator }}
                        options={lostLocationsOptions}
                        value={selectedOption}
                        onChange={(option) => setSelectedOption(option)}
                    />
                </Col>
                <Col className="d-flex align-items-center px-0 my-auto">
                    <Tooltip
                        title="Lost locations refer to locations that purchased from you previously but have not purchased ytd. This may include some locations that have left our platform."
                        arrow
                    >
                        <div className="py-auto">
                            <IoMdInformationCircle
                                className="text-size-18"
                                style={{ color: Theme().colors.accent }}
                            />
                        </div>
                    </Tooltip>
                </Col>
                <Col style={{ display: isDashboard ? "" : "none" }}>
                    <Button
                        block
                        outline={true}
                        style={{ float: "right", padding: "6px", fontSize: "14px" }}
                        color={Theme().colors.accentName}
                        onClick={() => onClickSeeMore("All Lost Locations", slideDrawerContents)}
                    >
                        See More
                    </Button>
                </Col>
            </Row>
            <Row className="me-2" style={{ height: "240px" }}>
                <AutoSizer>
                    {({ height, width }) => (
                        <Table
                            width={width}
                            height={height}
                            headerHeight={40}
                            rowHeight={40}
                            rowCount={lostLocationsData?.length < 5 ? lostLocationsData?.length : 5}
                            rowGetter={({ index }) => lostLocationsData?.[index]}
                            className="table topCategoriesTable border-grayLight text-size-14"
                            headerClassName="table-header"
                            rowClassName="table-row"
                        >
                            <Column
                                label="LOCATIONS"
                                dataKey="location_name"
                                width={usaIncluded ? width * 0.4 : width * 0.6}
                            />

                            {usaIncluded && (
                                <Column 
                                    label="TOTAL VOLUME ($)" 
                                    dataKey="total_volume" 
                                    width={width * 0.3} 
                                    cellRenderer={({ cellData }) => usCurrencyFormatter.format(cellData)} 
                                />
                            )}

                            <Column
                                label="CASES"
                                dataKey="total_cases"
                                width={usaIncluded ? width * 0.3 : width * 0.4}
                            />
                        </Table>
                    )}
                </AutoSizer>
            </Row>
        </Col>
    );
};

export default LostLocationsTable;